import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { UntypedFormControl, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin, of, filter, map, mergeMap, pluck } from 'rxjs';
import { ModalService } from '../../../core/modal/modal.service';
import { ListModalComponent, ListModalOutput } from '../../../shared/components/modals/list-modal/list-modal.component';
import { ModuleModalComponent } from '../../../shared/components/modals/modules/modules.component';
import { DisallowGroupDeleteModalComponent } from '../disallow-group-delete-modal/disallow-group-delete-modal.component';
import { Selected } from '../../../shared/selected.pipe';
import { PageHeaderDropdownItem } from '../../../shared/page-header/page-header.model';
import { UsersService } from '../../users/user.service';
import { UserPermissionsService } from '../../users/user-permissions.service';
import { AlertsService } from '../../../core/services/alerts.service';
import { User } from '../../../core/modules/users/users.types';
import { Group, NewGroupBody } from '../../../core/modules/groups/groups.types';
import { GroupsApiService } from '../../../core/modules/groups/groups-api.service';
import { RetailPlacesApiService } from '../../../core/modules/retail-places/retail-places-api.service';
import { RetailPlaceService } from '../../../core/services/retail-place.service';
import { RetailPlace } from '../../../core/modules/retail-places/retail-paces.types';
import { Permission } from '../../../core/modules/admin/admin.types';

type RetailPlaceWithSelectedState = RetailPlace & Selected;

@UntilDestroy()
@Component({
  selector: 'tax-group',
  templateUrl: './group-page.component.html',
  styleUrls: ['./group-page.component.sass'],
})
export class GroupPageComponent implements OnInit {
  dropdownItems: PageHeaderDropdownItem[] = [];

  group?: Group;
  groupTitle: UntypedFormControl = new UntypedFormControl('', Validators.required);
  isNewGroup = true;

  retailPlaces: RetailPlaceWithSelectedState[] = [];
  selectedModules: Permission[] = [];
  users: (User & Selected)[] = [];

  private isFullRetailPlaceAccess = false;

  constructor(
    private alertsService: AlertsService,
    private groupsApiService: GroupsApiService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private modalService: ModalService,
    private retailPlacesApiService: RetailPlacesApiService,
    private retailPlaceService: RetailPlaceService,
    private userPermissionsService: UserPermissionsService,
    private usersService: UsersService,
  ) {}

  ngOnInit(): void {
    this.getGroupData();
  }

  get displayedSelectedModules(): Permission[] {
    return this.userPermissionsService.getDisplayedSelectedPermissions(this.selectedModules);
  }

  back(): void {
    this.location.back();
  }

  openPermissionsModal(): void {
    this.modalService.openOld<Permission[]>(ModuleModalComponent, {
      selectedPermissions: this.selectedModules,
    }, {
      width: '740px',
    })
      .pipe(
        filter(permissions => Boolean(permissions)),
        untilDestroyed(this),
      )
      .subscribe((selectedPermissions) => {
        this.selectedModules = selectedPermissions;
      });
  }

  openRetailPlacesModal(): void {
    this.modalService.openOld<ListModalOutput<RetailPlaceWithSelectedState>>(
      ListModalComponent,
      {
        isFullAccess: this.isFullRetailPlaceAccess,
        type: 'retailPlaces',
        items: this.retailPlaces,
        propertiesMap: {
          description: 'address',
        },
      },
      {
        width: '740px',
      },
    )
      .pipe(
        filter(retailPlaces => Boolean(retailPlaces)),
        untilDestroyed(this),
      )
      .subscribe((retailPlaces) => {
        this.isFullRetailPlaceAccess = retailPlaces.isFullAccess;
        this.retailPlaces = retailPlaces.items;
      });
  }

  openUsersModal(): void {
    this.modalService.openOld<User[]>(
      ListModalComponent,
      {
        type: 'users',
        items: this.users,
        propertiesMap: {
          title: 'name',
          description: 'login',
        },
      },
      {
        width: '740px',
      },
    )
      .pipe(
        filter(users => Boolean(users)),
        untilDestroyed(this),
      )
      .subscribe((users) => {
        this.users = users;
      });
  }

  sendGroup(): void {
    const group: NewGroupBody | Group = {
      kkmGroupsPermissions: this.group?.kkmGroupsPermissions || [],
      permissions: this.selectedModules,
      retailPlaces: this.retailPlaces
        .filter(retailPlace => retailPlace.selected)
        .map(retailPlace => retailPlace.id),
      title: this.groupTitle.value,
      users: this.users.filter(user => user.selected),
    };
    if (this.isFullRetailPlaceAccess) {
      group.retailPlaces = [];
      group.permissions.push('retail.*');
    } else {
      group.permissions = group.permissions.filter(permission => permission !== 'retail.*');
    }

    if (!this.isNewGroup) {
      // @ts-ignore
      (group as Group).created = this.group?.created;
      // @ts-ignore
      (group as Group).id = this.group?.id;
      group.orgId = this.group?.orgId;
    }

    (this.isNewGroup
      ? this.groupsApiService.createGroup(group)
      : this.groupsApiService.updateGroup(group)
    )
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.router.navigate(['main/groups'])
            .then(() => {
              this.alertsService.addSuccessAlert({
                message: this.isNewGroup ? 'group.create.success' : 'group.update.success',
              });
            });
        },
      });
  }

  private getGroupData(): void {
    this.route.params
      .pipe(
        pluck('id'),
        map((id) => {
          const groupId = parseInt(id, 10);
          if (groupId !== 0) {
            this.isNewGroup = false;
            this.dropdownItems = [
              {
                action: this.removeGroup,
                icon: 'delete',
                title: 'group.remove',
              },
            ];
          }

          return groupId;
        }),
        mergeMap(groupId => forkJoin([
          this.isNewGroup ? of(undefined) : this.groupsApiService.fetchGroup(groupId),
          this.retailPlacesApiService.fetchRetailPlaces(),
          this.usersService.getUsers('', 0, 0),
        ])),
        untilDestroyed(this),
      )
      .subscribe(([group, retailPlaces, users]: [Group | undefined, RetailPlace[], User[]]) => {
        this.isFullRetailPlaceAccess = this.retailPlaceService.isFullRetailPlaceAccess(group?.permissions || []);

        if (group) {
          this.groupTitle.setValue(group.title);
          this.group = group;
        }

        this.retailPlaces = retailPlaces.map(retailPlace => ({
          ...retailPlace,
          selected: this.isFullRetailPlaceAccess || group?.retailPlaces.includes(retailPlace.id),
        }));

        this.users = users.map(user => ({
          ...user,
          selected: group?.users.some(selectedUser => selectedUser.id === user.id),
        }));

        this.selectedModules = group?.permissions || [];
      });
  }

  private removeGroup = (): void => {
    if (!this.group) {
      return;
    }

    if (this.group.users.length > 0) {
      this.modalService.openOld(DisallowGroupDeleteModalComponent, {
        group: this.group,
      });
    } else {
      this.groupsApiService.deleteGroup(this.group)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: () => {
            this.router.navigate(['main/groups'])
              .then(() => {
                this.alertsService.addSuccessAlert({
                  message: 'group.delete.success',
                });
              });
          },
        });
    }
  };
}
